<template>
   <footer v-if="footerData" class="bg-brand-dark py-16 text-white">
      <div class="container text-left">
         <h1 data-cy="footer-header" class="max-w-xs text-3xl">{{ footerData.heading }}</h1>

         <!-- footer contact -->
         <div class="mt-5">
            <!-- Intercom button -->
            <div v-if="contactButtons.length > 0" class="mt-8 flex flex-col">
               <template v-for="(contactButton, i) in contactButtons" :key="`contact-button-${i}`">
                  <DefaultButton
                     v-if="contactButton.action === 'intercom'"
                     class="w-fit"
                     :color="Theme.White"
                     @click="intercom.open()"
                  >
                     {{ contactButton.text }}
                  </DefaultButton>
               </template>
            </div>

            <!-- Contact details -->
            <div v-if="contactLinks" class="mt-6 inline-flex flex-col">
               <a
                  v-for="({ url, text }, i) in contactLinks"
                  :key="`contact-link-${i}`"
                  class="md:treading-relaxed mb-2 py-1 text-base underline last-of-type:mb-0 md:text-lg"
                  :href="url"
                  >{{ text }}
               </a>
            </div>
         </div>

         <!-- privacy + terms + powered by -->
         <div class="mt-20 block text-xs text-grey-20">
            <!-- Hamzah - remove v-if here when updating footer cookie links -->
            <div v-if="footerData.navLinks" class="flex flex-wrap gap-4">
               <!-- Privacy policy and terms of condition -->
               <span v-for="(footerLink, i) in footerData.navLinks" :key="`nav-link-${i}`" class="text-sm text-white">
                  <LinkElement v-bind="footerLink" />
               </span>
            </div>
            <!-- Site operated by -->
            <div class="mt-5 flex w-full flex-col justify-between md:flex-row">
               <!-- Copyright -->
               <p v-if="footerData.copyright" class="text-sm tracking-wide">
                  &copy; {{ currentYear }} {{ footerData.copyright }}
               </p>

               <!-- Powered by Sano - dependant on config -->
               <div v-if="footerData.showPoweredBy" class="mt-6 flex items-center justify-start md:mt-0">
                  <a v-if="footerData.poweredByLinkEnabled" href="https://sanogenetics.com/">
                     <IconSanoPoweredBy />
                     <span class="sr-only">{{ $t("system.powered_by_sano") }}</span>
                  </a>
                  <IconSanoPoweredBy v-else />
               </div>
            </div>
         </div>
      </div>
   </footer>
</template>

<script setup lang="ts">
import { inject } from "vue";
import { intercomKey } from "@/plugins/useIntercom";
import { type FooterData } from "@/data/api-layer/contentApi";
import { useBaseContentStore } from "@/stores//baseContentStore";
import { Theme, type LinkData, type ButtonData } from "@/types";

const intercom = inject(intercomKey);
const baseContentStore = useBaseContentStore();

const footerData = computed<FooterData>(() => {
   if (!baseContentStore.content) return null;

   return baseContentStore.content.footer;
});

const currentYear = new Date().getFullYear();

const contactLinks = computed(() => {
   if (!footerData.value.contactOptions) {
      console.warn("Footer data contact options does not exist");
      return [];
   }

   return footerData.value.contactOptions.filter((option) => option.type === "link") as LinkData[];
});

const contactButtons = computed(() => {
   if (!footerData.value.contactOptions) {
      console.warn("Footer data contact options does not exist");
      return [];
   }

   return footerData.value.contactOptions.filter((option) => option.type === "button") as ButtonData[];
});
</script>
